import { withRouter } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ENTITLEMENTS } from '../../application-core/constants';
import Errored from '../../core/Errored';
import TabbedSubmenu from '../../core/TabbedSubmenu.js';
import { UserConsumer } from '../../core/providers/UserProvider';
import { globalStyles } from '../../core/style/gstheme';
import GS from '../../grafsync/grafsync';
import ActionText from '../ActionText';
import { checkResourceEntitlements, getGraphFromProject, makeCancelable } from '../utils/utils';
import FindProjectDialog from './FindProjectDialog';

// We expect a project managing component to be injected to allow for different types of associated project

const styles = () => {
    return Object.assign({
        aop: {
            marginRight: 30,
            padding: 12,
            backgroundColor: '#ffffff',
            borderRadius: 4,
            minHeight: 520
        }
    }, globalStyles);
};

class ManageAssociatedProject extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            projectDetail: [],
            deletedAssociations: [],
            isErrored: false
        };
        this.cancellable = null;
    }

    loadData(iris, options) {
        this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph));
        return this.cancellable;
    }

    loadDataFromGraph(iriData, options = {}) {
        this.cancellable1 = makeCancelable(GS.loadDataFromGraph(iriData, options));
        return this.cancellable1;
    }

    componentDidMount() {
        const application = this.props.location.pathname.startsWith("/vocabularymanager") ?
            "vm"
            :
            this.props.location.pathname.startsWith("/datamanager") ?
                "dm"
                :
                "om";
        GS.setStore(application, this.props.location.pathname + this.props.location.hash);
        this.collectProjects();
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    collectProjects = async () => {
        try {
            GS.handle("load-aop");
            const { data } = this.props;
            if (!data.ontologyProjectAssociation || data.ontologyProjectAssociation.length === 0) return;
            await this.loadData(data.ontologyProjectAssociation).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            let projectInfo = [];
            for (let association of data.ontologyProjectAssociation) {
                if (!this.state.deletedAssociations.includes(association)) {
                    if (GS.isLoaded(association, this.props.graph)) {
                        const associationData = GS.data(association, this.props.graph);
                        projectInfo.push({
                            iri: associationData.associatedProject,
                            graph: getGraphFromProject(associationData.associatedProject),
                            association
                        });
                    }
                }
            }
            await this.loadDataFromGraph(projectInfo).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            let projectDetail = [];
            for (let project of projectInfo) {
                if (GS.isLoaded(project.iri, project.graph)) {
                    const projectData = GS.data(project.iri, project.graph);
                    projectData.association = project.association;
                    projectDetail.push(projectData);
                }
            }
            let sortedProjectDetail = projectDetail.sort((aData, bData) => {
                const aLabel = aData.title[aData.defaultLang];
                const bLabel = bData.title[bData.defaultLang];
                if (aLabel.toLowerCase() < bLabel.toLowerCase()) return -1;
                if (bLabel.toLowerCase() < aLabel.toLowerCase()) return 1;
                return 0;
            });
            this.setState({ projectDetail: sortedProjectDetail });
        }
        catch (e) {
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e, statementLoading: false, isLoadingStatments: false });
        }
    };

    onAddProjectAssociation = async (projectIRI) => {
        // There is a small chance that a project could get added twice but it won't break anything
        if (this.props.ontologyProjects && this.props.ontologyProjects.includes(projectIRI)) return;
        const associationIRI = "https://grafsync.graphifi.com/data/association/" + uuidv4();
        const newAssociation = {
            id: associationIRI,
            type: "OntologyProjectAssociation",
            associatedProject: projectIRI
        };
        await GS.insertResource(newAssociation, associationIRI, this.props.graph, [this.props.data.id]);
        GS.addProperty(this.props.data.id, this.props.graph, 'ontologyProjectAssociation', associationIRI);
        this.collectProjects();
    };

    onDeleteProjectAssociation = (associationIRI) => {
        GS.deleteResource(associationIRI, this.props.graph);
        let newDeletedAssociations = [...this.state.deletedAssociations];
        newDeletedAssociations.push(associationIRI);
        this.setState({ deletedAssociations: newDeletedAssociations }, () => {
            this.collectProjects();
        });
    };

    render() {

        const { classes, data, associatedProjectComponent, graph, lockedGraphs, projectType } = this.props;
        const { projectDetail, isErrored } = this.state;
        const AssociatedProject = associatedProjectComponent;

        if (isErrored) return <Errored dataTest="map-error" data={isErrored} />;

        const { defaultLang } = data;
        const requiredEntitlement = projectType === "TaxonomyProject" ?
            ENTITLEMENTS.EDIT_TAXONOMY_PROJECT
            :
            projectType === "OntologyProject" ?
                ENTITLEMENTS.EDIT_ONTOLOGY_PROJECT
                :
                ENTITLEMENTS.EDIT_DATA_PROJECT;
        const isReadOnly = checkResourceEntitlements(data, requiredEntitlement);

        const projectTitles = projectDetail.map(pd => pd.title[pd.defaultLang]);

        return (
            <UserConsumer>
                {({ entitlements, modules }) => (
                    !entitlements.includes(ENTITLEMENTS.VIEW_ONTOLOGY_PROJECT) ?
                        <Typography data-test="no-permissions" variant="body2">No permission to view ontology projects.</Typography>
                        :
                        <div className={classes.aop}>
                            <div data-test="search-filtering" className={classes.filterArea}>
                                <Grid container spacing={2} classes={
                                    {
                                        "spacing-xs-2": this.props.classes.fullGrid
                                    }
                                }>
                                    <Grid item xs={9}>
                                        <Typography className={classes.mainHeading} style={{ paddingTop: 0 }}>Template Manager</Typography>
                                    </Grid>
                                    <Grid item xs={3} style={{ textAlign: 'right' }}>
                                        {!isReadOnly && projectDetail && projectDetail.length > 0 &&
                                            <FindProjectDialog
                                                project={data.id}
                                                graph={getGraphFromProject(data.id)}
                                                onAddProject={this.onAddProjectAssociation}
                                                defaultLang={defaultLang}
                                            />
                                        }
                                    </Grid>
                                </Grid>
                            </div>
                            <div data-test="associated-projects" style={{ paddingBottom: 30 }}>
                                {projectDetail && projectDetail.length > 0 ?
                                    <TabbedSubmenu
                                        tabs={projectTitles}
                                        key={projectTitles.length}>
                                        {projectDetail.map((detail) =>
                                            <AssociatedProject
                                                key={detail.association}
                                                id={detail.association}
                                                modules={modules}
                                                requiredEntitlement={requiredEntitlement}
                                                projectType={projectType}
                                                lockedGraphs={lockedGraphs}
                                                graph={graph}
                                                defaultLang={defaultLang}
                                                onDeleteProject={this.onDeleteProjectAssociation}
                                            />
                                        )}
                                    </TabbedSubmenu>
                                    :
                                    <div style={{ width: '100%', marginTop: '60px', textAlign: 'center' }}>
                                        <div style={{ marginBottom: '40px' }}>
                                            <img style={{ height: 150 }} src="/images/ontology-manager-black.svg" alt="" />
                                            {projectType === "TaxonomyProject" ?
                                                <ActionText offset="40px">Enrich your taxonomies using ontologies.</ActionText>
                                                :
                                                projectType === "DataProject" ?
                                                    <ActionText offset="40px">Enrich your data using ontologies.</ActionText>
                                                    :
                                                    null
                                            }
                                        </div>
                                        {!isReadOnly &&
                                            <FindProjectDialog
                                                project={data.id}
                                                graph={getGraphFromProject(data.id)}
                                                onAddProject={this.onAddProjectAssociation}
                                                defaultLang={defaultLang}
                                            />
                                        }
                                    </div>
                                }
                            </div>
                        </div>
                )}
            </UserConsumer>

        );
    }
}

ManageAssociatedProject.propTypes = {
    associatedProjectComponent: PropTypes.object.isRequired,
    projectType: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    graph: PropTypes.string.isRequired,
    lockedGraphs: PropTypes.array,
};

export default withRouter(withStyles(styles)(ManageAssociatedProject));